import { Delete, LoginOutlined } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { Button } from "@mui/material";
import * as React from "react";
import { assetService } from "../../../Core/Api/services";
import { DbObjectCollectionInfos } from "../../../Core/Api/Types/db-object-collection-infos";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { ModalContext } from "../../Modals";
import ModalCreateShare from "../../Modals/ModalCreateShare/modal-create-share";
import SelectModeBanner from "../SelectModeBanner";
import { IsDriveObject, IsObjectShareable } from "../../../Core/Utils/asset-object-utils";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import ModalCreateOrUpdateExternalAccess, {
  ModalCreateOrUpdateExternalAccessElementProps,
} from "../../Modals/ModalCreateOrUpdateExternalAccess/modal-create-or-update-external-access";
import CreateAccessButton from "../../Access/Shared/create-access-button";
import { useTranslation } from "react-i18next";

export interface CollectionBannerProps {
  handleCloseClick: () => void;
  removePostAction: () => void;
  collections: DbObjectCollectionInfos[];
}

const CollectionBanner = (props: CollectionBannerProps) => {
  const { t } = useTranslation();
  const { collections, handleCloseClick, removePostAction } = props;
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const { addObjectByIdsToBasket } = useBasketActions();
  const enqueueSnackbar = useCustomSnackbar();

  const handleRemoveFromCollection = () => {
    collections.forEach((collection) => {
      assetService.DeleteCollection(collection.id).then((data) => {
        if (!data.success) return;
        enqueueSnackbar(t("alert:success.collectionsRemoved"), "success");
        removePostAction();
        handleCloseClick();
      });
    });
  };

  const handleAddToBasketClick = () => {
    collections.forEach((collection) => {
      if (collection.objectIds) addObjectByIdsToBasket(collection.objectIds);
    });
  };

  const shareableObjectsIds = React.useMemo(() => {
    return collections.flatMap(
      (collection) => collection.objectInfos?.filter(IsObjectShareable).map((asset) => asset.id) || []
    );
  }, [collections]);

  const openNewShareModal = () => {
    if (!openModal) return;
    openModal(ModalCreateShare, { assetIds: shareableObjectsIds, isIndependentItems: true });
  };

  const driveObjects = React.useMemo(() => {
    return collections
      .filter((c) => c.objectInfos?.some((o) => IsDriveObject(o)))
      .flatMap((collection) => collection.objectInfos || []);
  }, [collections]);

  return (
    <SelectModeBanner
      text={t("banner:body.itemSelected", { count: collections.length })}
      onCloseClick={handleCloseClick}
    >
      {shareableObjectsIds.length > 0 && (
        <Button startIcon={<ShareOutlinedIcon />} onClick={openNewShareModal} variant="whiteSoft">
          {t("common:share")}
        </Button>
      )}
      {driveObjects.length > 0 && <CreateAccessButton assets={driveObjects} variant="whiteSoft" />}
      {collections.every((coll) => !coll.isShared) && (
        <>
          <Button startIcon={<Delete />} onClick={handleRemoveFromCollection} variant="contained" color="error">
            {t("common:delete")}
          </Button>
        </>
      )}
      <Button variant="white" startIcon={<AddIcon />} onClick={handleAddToBasketClick}>
        {t("common:addToBasket")}
      </Button>
    </SelectModeBanner>
  );
};

export default CollectionBanner;
